import request from 'api/request';
import {handleError, formatter} from 'helpers';
import {MAX_NUM_SUGGESTIONS, Sorting} from 'helpers/constants';

import * as alerts from './alertActions';
import reloadInventory from './inventoryActions';
import {priceListActionTypes as actions} from './actionTypes';
import {asyncLoad} from '.';

export const updatePaging = ({pageNumber, pageSize}) => ({
  type: actions.PRICE_LIST_PAGE_CHANGED,
  pageNumber,
  pageSize,
});

export const changeSort = ({sortColumn, sortOrder}) => ({
  type: actions.PRICE_LIST_SORT_CHANGED,
  sortColumn,
  sortOrder,
});

export const checkAll = isChecked => ({
  type: actions.PRICE_LIST_ALL_CHECKED_CHANGED,
  isChecked,
});

export const checkItem = item => ({
  type: actions.PRICE_LIST_ITEM_CHECK_CHANGED,
  item,
});

export const changeFilterOption = filterOption => ({
  type: actions.PRICE_LIST_FILTER_OPTION_CHANGED,
  filterOption,
});

export const changeSearchString = searchString => ({
  type: actions.PRICE_LIST_SEARCH_STRING_CHANGED,
  searchString,
});

export const getPriceList = ({
  actionType = actions.GET_PRICE_LIST_SUCCESS,
  filterOption,
  itemTypeId = null,
  pageInfo = {},
  searchString = '',
  sortInfo = {},
  excludeItemTypes = '6,7',
}) => dispatch => {
  const {sortColumn, sortOrder} = sortInfo;
  const {pageSize, pageNumber} = pageInfo;

  let url = '/priceList';

  url += `?ExcludeItemTypes=${excludeItemTypes}`;
  url += `&pageSize=${pageSize}&rowIndex=${(pageNumber - 1) * pageSize}`;

  if (itemTypeId !== null) {
    url += `&Type=${itemTypeId}`;
  }

  if (sortColumn) {
    const sortCamelCase = `${sortColumn
      .charAt(0)
      .toLowerCase()}${sortColumn.substring(1)}`;
    const apiSortOrder = sortOrder === Sorting.desc ? '-' : '';
    url += `&sort=${apiSortOrder}${sortCamelCase}`;
    if (sortCamelCase !== 'qualifiedName') {
      url += ',qualifiedName';
    }
  }

  switch (filterOption) {
    case 'DeletedOnly':
      url = `${url}&ShowOnlyDeleted=true&LowInventory=false`;
      break;
    case 'ActiveLowInventory':
      url = `${url}&ShowOnlyDeleted=false&LowInventory=true`;
      break;
    default:
      url = `${url}&ShowOnlyDeleted=false&LowInventory=false`;
      break;
  }

  if (searchString) {
    url = `${url}&name=${encodeURIComponent(searchString)}`;
  }

  dispatch({type: actions.PRICE_LIST_LOADING});

  return request({url})
    .then(resp => {
      if (resp.status >= 500) {
        throw new Error('Server error while getting price list items');
      }
      const results =
        resp && resp.data && resp.data.results ? resp.data.results : [];
      const totalRows =
        resp && resp.data && resp.data.totalRows ? resp.data.totalRows : 0;
      const items = results.map(i => ({
        ...i,
        description: formatter.formatDescription(i.description),
        price: formatter.formatRate(i.rate),
      }));

      dispatch({
        type: actionType,
        items,
        totalRows,
        searchString,
      });
      dispatch({type: actions.PRICE_LIST_LOADING_COMPLETE});

      return items;
    })
    .catch(error => {
      handleError('Unable to load price list items', error, dispatch);
      dispatch({type: actions.PRICE_LIST_LOADING_ERROR});
    });
};

export const loadPriceList = (dispatch, getState) => {
  const state = getState();
  const {allItems} = state.priceList;
  dispatch(
    getPriceList({
      filterOption: allItems.filterOption,
      pageInfo: allItems.pageInfo,
      searchString: allItems.searchString,
      sortInfo: allItems.sortInfo,
    }),
  );
};

// Filter/Search
export const changeFilter = filterValue => (dispatch, getState) => {
  const state = getState();
  const {allItems} = state.priceList;
  dispatch(
    getPriceList({
      actionType: actions.GET_PREVIEW_LIST_SUCCESS,
      filterOption: allItems.filterOption,
      pageInfo: {pageNumber: 1, pageSize: MAX_NUM_SUGGESTIONS},
      searchString: filterValue,
      sortInfo: {sortColumn: 'name', sortOrder: Sorting.asc},
    }),
  );
};

export const deleteItems = () => (dispatch, getState) => {
  const state = getState();
  const {allItems} = state.priceList;
  const itemsToDelete = allItems.items.filter(i => i.isChecked);
  dispatch({type: actions.PRICE_LIST_DELETE_LOADING});
  return request({
    url: `/PriceList/`,
    body: {
      Operation: 'delete',
      Id: null,
      Values: itemsToDelete.map(i => ({
        priceListItemId: i.priceListItemId,
        itemTypeId: i.itemTypeId,
      })),
    },
    method: 'PUT',
    batch: true,
  })
    .then(response => {
      if (!response || !response.data || !response.data.hasErrors) {
        dispatch(alerts.success('Delete successful'));
        reloadInventory(dispatch, getState);
      } else if (response.data.results) {
        response.data.results.forEach(i => {
          if (!i.success) {
            dispatch(alerts.error(`Error: ${i.errorMessage}`));
          }
        });
      }
      dispatch({type: actions.PRICE_LIST_DELETE_COMPLETE});
    })
    .catch(error => {
      handleError('Unable to remove price list items', error, dispatch);
      dispatch({type: actions.PRICE_LIST_DELETE_COMPLETE});
    });
};

export const getItemTypes = () => dispatch => {
  const url = '/priceListType';

  return request({url, includePortal: false})
    .then(data => {
      const results =
        data && data.data && data.data.results ? data.data.results : [];
      dispatch({
        type: actions.LOAD_ITEM_TYPES_SUCCESS,
        items: results,
      });
    })
    .catch(error => {
      handleError('Unable to load item types', error, dispatch);
    });
};

// Sort Item Locations Table
export const sortItemLocationsTable = (
  itemId,
  sortColumn,
  sortOrder,
) => dispatch =>
  dispatch({
    type: actions.PRICE_LIST_LOCATIONS_TABLE_SORT_CHANGED,
    itemId,
    sortOrder,
    sortColumn,
  });

// Get Parent Count
export const getParentCount = selectedItems => dispatch => {
  const url = `/priceList/`;
  const body = {
    operation: 'delete-count',
    values: selectedItems.map(x => {
      return {
        priceListItemId: x.priceListItemId || x.partId,
        itemTypeId:
          x.itemType && x.itemType.toLowerCase() === 'product'
            ? 1
            : x.itemTypeId,
      };
    }),
  };

  return request({
    url,
    method: 'PUT',
    body,
    batch: true,
  })
    .then(data => {
      return data.data;
    })
    .catch(error =>
      handleError('Unable to get parent count.', error, dispatch),
    );
};

export const exportPriceListCSV = (searchString, filterOption) => dispatch => {
  const queryParams = {
    name: searchString || null,
    showOnlyDeleted: filterOption === 'DeletedOnly',
    lowInventory: filterOption === 'ActiveLowInventory',
    excludeItemTypes: '6,7',
  };

  const queryString = Object.keys(queryParams).reduce((acc, key) => {
    const val = queryParams[key];
    return val !== null ? `${acc}${key}=${encodeURIComponent(val)}&` : acc;
  }, '');

  return request({
    url: `/CSV/PriceList?${queryString}`,
    headers: {contentType: 'text/csv'},
  })
    .then(result => {
      if (result.status === 200) {
        dispatch(alerts.success(result.data.message));
        return true;
      }
      return false;
    })
    .catch(error => handleError('Error exporting to CSV', error, dispatch));
};

export const loadPriceListItems = (
  searchString = '',
  itemTypeId = null,
  includeTax = false,
) => async () => {
  let url = `/priceList${
    itemTypeId !== null || !searchString || searchString.length < 2
      ? `?sort=qualifiedName&excludeItemTypes=${includeTax ? '' : '7'}`
      : `/search?includeTax=${includeTax}`
  }`;
  if (searchString) {
    url += `${
      itemTypeId === null ? '&searchString' : '&name'
    }=${encodeURIComponent(searchString)}`;
  }
  if (itemTypeId !== null) {
    url += `&type=${itemTypeId}`;
  }
  return asyncLoad({url});
};



// WEBPACK FOOTER //
// ./src/actions/priceListActions.js